/**
 * Dunea Remote session crypto — EXACT mirror of the desktop sidecar's
 * remote-crypto.ts and the mobile app's lib/crypto.ts. NaCl box
 * (X25519 + XSalsa20-Poly1305) via tweetnacl; browsers provide
 * crypto.getRandomValues so no PRNG shim is needed.
 */
import nacl from 'tweetnacl';
import { decodeBase64, encodeBase64 } from 'tweetnacl-util';

export interface KeyPair {
  publicKey: Uint8Array;
  secretKey: Uint8Array;
}

export const b64 = {
  enc: (u: Uint8Array): string => encodeBase64(u),
  dec: (s: string): Uint8Array => decodeBase64(s),
};

export function generateIdentity(): KeyPair {
  return nacl.box.keyPair();
}

export function newEphemeral(): KeyPair {
  return nacl.box.keyPair();
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Seal a payload long-term → long-term (hello only). */
export function sealIdentity(payload: object, mySecret: Uint8Array, peerPublic: Uint8Array): { ct: string; n: string } {
  const nonce = nacl.randomBytes(nacl.box.nonceLength);
  const msg = encoder.encode(JSON.stringify(payload));
  const ct = nacl.box(msg, nonce, peerPublic, mySecret);

  return { ct: b64.enc(ct), n: b64.enc(nonce) };
}

export function openIdentity<T>(ct: string, n: string, peerPublic: Uint8Array, mySecret: Uint8Array): T | null {
  let opened: Uint8Array | null;

  try {
    opened = nacl.box.open(b64.dec(ct), b64.dec(n), peerPublic, mySecret);
  } catch {
    return null;
  }

  if (!opened) {
    return null;
  }

  try {
    return JSON.parse(decoder.decode(opened)) as T;
  } catch {
    return null;
  }
}

export interface SessionCrypto {
  seal(payload: object): { ct: string; n: string };
  open<T>(ct: string, n: string): T | null;
}

/**
 * Established after both hellos: bulk frames use ephemeral keys. The counter
 * rides INSIDE the sealed payload (`c`) — receiver enforces monotonicity.
 */
export function createSession(myEphSecret: Uint8Array, peerEphPublic: Uint8Array): SessionCrypto {
  let sendCounter = 0;
  let lastRecv = 0;
  const shared = nacl.box.before(peerEphPublic, myEphSecret);

  return {
    seal(payload: object) {
      const body = { ...payload, c: ++sendCounter };
      const nonce = nacl.randomBytes(nacl.box.nonceLength);
      const ct = nacl.box.after(encoder.encode(JSON.stringify(body)), nonce, shared);

      return { ct: b64.enc(ct), n: b64.enc(nonce) };
    },
    open<T>(ct: string, n: string): T | null {
      let opened: Uint8Array | null;

      try {
        opened = nacl.box.open.after(b64.dec(ct), b64.dec(n), shared);
      } catch {
        return null; // malformed wire input — never throw
      }

      if (!opened) {
        return null;
      }

      try {
        const parsed = JSON.parse(decoder.decode(opened)) as T & { c?: number };

        if (typeof parsed.c !== 'number' || parsed.c <= lastRecv) {
          return null;
        }

        lastRecv = parsed.c;

        return parsed;
      } catch {
        return null;
      }
    },
  };
}

/** 4-word SAS fingerprint — MUST match the server's devices.service, the sidecar, and the app. */
// prettier-ignore
export const SAS_WORDS = ['acid','acorn','alarm','alien','amber','anchor','ankle','apple','arrow','atlas','atom','autumn','bacon','badge','bagel','bamboo','banana','banjo','barrel','basil','beacon','beaver','bell','berry','bike','birch','bison','blade','blanket','blossom','bolt','bonfire','boot','bottle','box','brass','bread','brick','bridge','brook','broom','bubble','bucket','bugle','bunny','butter','button','cabin','cable','cactus','camel','candle','canoe','canyon','carbon','card','cargo','carrot','castle','cedar','chalk','cherry','chess','chime','cider','circle','claw','clay','cliff','clock','cloud','clover','cobalt','coconut','comet','compass','copper','coral','cotton','cradle','crane','crater','crayon','cricket','crown','crystal','cube','cyclone','daisy','dart','dawn','deer','delta','denim','desk','dew','diamond','dice','dime','dinghy','dome','donkey','door','dragon','drum','dune','eagle','earth','echo','eel','ember','engine','falcon','feather','fern','fiddle','field','fig','finch','fjord','flame','flint','flute','fog','forest','fossil','fox','frost','galaxy','garden','garlic','gate','gecko','gem','geyser','ginger','glacier','glove','gong','goose','grape','grove','guitar','hammer','harbor','hawk','hazel','helmet','heron','hill','honey','hoof','horn','iceberg','iguana','indigo','ink','iris','iron','island','ivory','jade','jaguar','jasmine','jelly','jewel','jungle','juniper','kayak','kettle','kite','kiwi','knot','lagoon','lantern','lark','lava','leaf','lemon','lily','lime','lion','lizard','llama','lotus','lunar','magnet','mango','maple','marble','meadow','melon','mesa','mint','mirror','monkey','moon','moss','moth','mountain','mule','mustard','nest','nickel','night','north','nutmeg','oak','oasis','ocean','olive','onion','opal','orange','orchid','otter','owl','oyster','palm','panda','paper','peach','pearl','pebble','pepper','piano','pigeon','pine','pixel','plum','pond','poppy','prairie','prism','pumpkin','quartz','quill','rabbit','raft','rain','raven','reef','ribbon','rice','ridge','river','robin','rocket','rose','ruby','saddle','sage','salt','sand','sapphire','scarf','seal','shell','silver','sky','slate','snow','spark','spice','spring','spruce','squid','star','stone','storm','sugar','summit','sun','swan','thorn','tiger','topaz','torch','trail','tulip','turtle','vela','violet','walnut','wave','willow','wolf','yarn','zebra'];

export function fingerprintWords(publicKeyB64: string): string {
  const h = nacl.hash(encoder.encode(publicKeyB64)); // sha512
  return [h[0], h[1], h[2], h[3]].map((byte) => SAS_WORDS[byte % SAS_WORDS.length]).join(' ');
}
